import { useCallback, useId, useRef, useState, type RefObject } from "react";

// The open-state of the composer's "+" — what it offers (a file, a playlist, an
// article, and on desktop the answer mode) and which of its views is showing. The composer renders the control
// and the panel in two different places (the button row / the strip above the
// card), so the state cannot live in either half: it belongs to the surface that
// composes them, via this hook.
//
// The article view carries its own search string: it is the panel's local UI
// state (the query is not a reference until the user taps a result), and it must
// reset when the panel closes so reopening never shows a stale search.

export type ChatComposerAddView =
  | { view: "closed" }
  | { view: "menu" }
  | { view: "playlists" }
  | { view: "articles"; query: string }
  | { view: "modes" };

export type ChatComposerAdd = {
  state: ChatComposerAddView;
  // `id` of the panel, for the control's `aria-controls`.
  panelId: string;
  open: () => void;
  close: () => void;
  showPlaylists: () => void;
  showArticles: () => void;
  showModes: () => void;
  showMenu: () => void;
  setArticleQuery: (query: string) => void;
  // The OS file picker. Its `<input>` rides with the control rather than the
  // panel, because picking closes the panel — an input unmounted mid-click never
  // opens.
  fileInputRef: RefObject<HTMLInputElement>;
  pickFile: () => void;
};

export function useChatComposerAdd(): ChatComposerAdd {
  const [state, setState] = useState<ChatComposerAddView>({ view: "closed" });
  const panelId = useId();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const pickFile = useCallback(() => {
    setState({ view: "closed" });
    const input = fileInputRef.current;
    if (input === null)
      throw new Error("should not happen: the file input is unmounted");
    input.click();
  }, []);

  return {
    state,
    panelId,
    open: useCallback(() => {
      setState({ view: "menu" });
    }, []),
    close: useCallback(() => {
      setState({ view: "closed" });
    }, []),
    showPlaylists: useCallback(() => {
      setState({ view: "playlists" });
    }, []),
    showArticles: useCallback(() => {
      setState({ view: "articles", query: "" });
    }, []),
    showModes: useCallback(() => {
      setState({ view: "modes" });
    }, []),
    showMenu: useCallback(() => {
      setState({ view: "menu" });
    }, []),
    setArticleQuery: useCallback((query: string) => {
      setState({ view: "articles", query });
    }, []),
    fileInputRef,
    pickFile,
  };
}
